import type { PlaygroundRun } from '@shared/types'

import { EC2_ENABLED } from '@/lib/featureFlags'

import GeminiIcon from '@/assets/svgs/tech/gemini.svg?react'

import ImageIcon from '../assets/svgs/image.svg?react'
import EC2Icon from '../assets/svgs/tech/ec2.svg?react'
import BackgroundIcon from '../assets/svgs/tools/background.svg?react'
import CharacterIcon from '../assets/svgs/tools/character.svg?react'
import CompositeIcon from '../assets/svgs/tools/composite.svg?react'
import InpaintIcon from '../assets/svgs/tools/inpaint.svg?react'
import OutpaintIcon from '../assets/svgs/tools/outpaint.svg?react'
import PromptIcon from '../assets/svgs/tools/prompt.svg?react'
import RemoveBackgroundIcon from '../assets/svgs/tools/remove-background.svg?react'
import UpscaleIcon from '../assets/svgs/tools/upscale.svg?react'
import type { Tool } from '../components/ToolsGrid'

export const tools: Tool[] = [
  {
    title: 'FreeFlow',
    description: 'Create an image from any idea. Add reference images only when they help guide the result.',
    icon: PromptIcon,
    cornerIcon: GeminiIcon,
    href: '/playground/freeflow',
    category: 'Create',
    features: ['Open-ended image prompts', 'Optional reference images', 'Continue from a result']
  },
  {
    title: 'Character Generation [Nano Banana]',
    description: 'Choose a character and describe a pose or action to create a new image.',
    icon: CharacterIcon,
    cornerIcon: GeminiIcon,
    href: '/playground/character-nanobanana',
    category: 'Create',
    features: [
      'Choose a pose or action', //
      'Add reference images',
      'High-resolution square images'
    ]
  },
  {
    title: 'Character Generation [WAN]',
    description: 'Create a 3D character on a clean white background. Describe the pose or action to get started.',
    icon: CharacterIcon,
    cornerIcon: EC2Icon,
    href: '/playground/character',
    category: 'Create',
    disabled: !EC2_ENABLED,
    disabledReason: 'Character generation is unavailable right now.',
    features: ['3D character on white', 'Sticker-style images', 'Square image output']
  },
  {
    title: 'Background Generation',
    description:
      'Create a scene that fits your character. Choose a character or upload an image, then describe the setting.',
    icon: BackgroundIcon,
    cornerIcon: GeminiIcon,
    href: '/playground/background',
    category: 'Create',
    features: [
      'Scenes that fit your character', //
      'Optional reference images',
      'Fine-tune the result'
    ]
  },
  {
    title: 'Inpainting',
    description:
      'Brush over the area you want to change, then describe the edit. Leave it unmarked to update the whole image.',
    icon: InpaintIcon,
    cornerIcon: GeminiIcon,
    href: '/playground/inpaint',
    category: 'Edit',
    features: [
      'Edit one area or the whole image', //
      'Brush over the change',
      'Keep the rest of the image intact'
    ]
  },
  {
    title: 'Composition',
    description:
      'Place a character into a background. Upload both images, then choose the look and details of the scene.',
    icon: CompositeIcon,
    cornerIcon: GeminiIcon,
    href: '/playground/composition',
    category: 'Edit',
    features: [
      'Blend character and background', //
      'Choose a style and scene details',
      'Fits the background shape'
    ]
  },
  {
    title: 'Outpaint',
    description:
      'Give your image more room. Choose a new frame, position the subject, and describe what should appear around it.',
    icon: OutpaintIcon,
    cornerIcon: GeminiIcon,
    href: '/playground/outpaint',
    category: 'Edit',
    features: ['Expand space around an image', 'Guide subject placement', 'Choose the output size']
  },
  {
    title: 'AI Upscale',
    description: 'Make an image larger while keeping its composition and details close to the original.',
    icon: UpscaleIcon,
    cornerIcon: GeminiIcon,
    href: '/playground/upscale',
    category: 'Finish',
    features: ['Keep the original composition', 'Choose the output size', 'Compare before and after']
  },
  {
    title: 'Remove Background',
    description: 'Upload an image and create a clean transparent cutout.',
    icon: RemoveBackgroundIcon,
    cornerIcon: GeminiIcon,
    href: '/playground/remove-background',
    category: 'Finish',
    features: ['Transparent background', 'Clean cutout', 'Ready to download']
  },
  {
    title: 'Prompt Enrichment',
    description: 'Turn a short idea into a fuller image description before you create an image.',
    icon: PromptIcon,
    cornerIcon: GeminiIcon,
    href: '/playground/enrichment',
    category: 'Prompt tools',
    features: [
      'Start with a short idea', //
      'Review the fuller description'
    ]
  }
]

/** Small creative utilities that run entirely in the browser and never create Playground runs. */
export const localTools: Tool[] = [
  {
    title: 'Mask Creator',
    description: 'Paint the area you want to edit and download a mask without uploading your image.',
    icon: InpaintIcon,
    href: '/tools/mask',
    features: ['Works entirely in your browser', 'Black-and-white PNG mask', 'Live mask preview']
  },
  {
    title: 'Outpaint Mask',
    description: 'Place and scale an image in a larger frame, then download the layout guide.',
    icon: OutpaintIcon,
    href: '/tools/outpaint-mask',
    features: ['Works entirely in your browser', 'Choose a frame shape', 'PNG layout guide']
  },
  {
    title: 'Social Media Preview',
    description: 'Preview and position one image in common social-media formats without uploading it.',
    icon: ImageIcon,
    href: '/tools/social-media-preview',
    features: ['Works entirely in your browser', 'Independent format placement', 'Story and TikTok safe areas']
  }
]

/** Saved-run generators for reusable character reference images. */
export const referenceTools: Tool[] = [
  {
    title: 'Character Sheet',
    description: 'Create a character sheet from identity images, with an eight-view layout by default.',
    icon: CharacterIcon,
    cornerIcon: GeminiIcon,
    href: '/tools/character-sheet',
    category: 'AI reference generation',
    features: [
      'Full body, upper body, and head views',
      'Saved references or uploaded images',
      'Download to upload in character settings'
    ]
  },
  {
    title: 'Scale Reference',
    description: 'Show a character’s size beside familiar objects in one comparison grid.',
    icon: CompositeIcon,
    cornerIcon: GeminiIcon,
    href: '/tools/scale-reference',
    category: 'AI reference generation',
    features: [
      'Four comparisons by default',
      'Editable size and comparison brief',
      'Download to upload in character settings'
    ]
  }
]

/** Local image preparation helpers, separate from saved reference generation. */
export const imageUtilities: Tool[] = localTools.map((tool) => ({ ...tool, category: 'Browser-only helpers' }))

// Tool type string literal
export const TOOL_TYPES = [
  'enrichment',
  'character-wan',
  'character-nanobanana',
  'background',
  'inpaint',
  'outpaint',
  'upscale',
  'remove-background',
  'composition',
  'freeflow',
  'reference-builder'
] as const

export type ToolType = (typeof TOOL_TYPES)[number]

// Label mapping for quick lookup by tool type
export const TOOL_LABELS: Record<
  ToolType,
  { key: ToolType; label: string; showOnFilters: boolean; icon: typeof PromptIcon }
> = {
  enrichment: {
    key: 'enrichment',
    label: 'Prompt Enrichment',
    showOnFilters: false,
    icon: PromptIcon
  },
  'character-wan': {
    key: 'character-wan',
    label: 'Character [WAN]',
    showOnFilters: true,
    icon: CharacterIcon
  },
  'character-nanobanana': {
    key: 'character-nanobanana',
    label: 'Character [Nano Banana]',
    showOnFilters: true,
    icon: CharacterIcon
  },
  background: {
    key: 'background',
    label: 'Background Generation',
    showOnFilters: true,
    icon: BackgroundIcon
  },
  inpaint: {
    key: 'inpaint',
    label: 'Inpainting',
    showOnFilters: true,
    icon: InpaintIcon
  },
  outpaint: {
    key: 'outpaint',
    label: 'Outpaint',
    showOnFilters: true,
    icon: OutpaintIcon
  },
  upscale: {
    key: 'upscale',
    label: 'AI Upscale',
    showOnFilters: true,
    icon: UpscaleIcon
  },
  'remove-background': {
    key: 'remove-background',
    label: 'Remove Background',
    showOnFilters: true,
    icon: RemoveBackgroundIcon
  },
  composition: {
    key: 'composition',
    label: 'Composition',
    showOnFilters: true,
    icon: CompositeIcon
  },
  'reference-builder': {
    key: 'reference-builder',
    label: 'Reference Builder',
    showOnFilters: true,
    icon: CharacterIcon
  },
  freeflow: {
    key: 'freeflow',
    label: 'FreeFlow',
    showOnFilters: true,
    icon: PromptIcon
  }
}

// Helper to get tool label
export function getToolTitle(toolType: ToolType): string {
  return TOOL_LABELS[toolType].label
}

/** Reference runs share processing but keep their generator identity in history. */
export function getPlaygroundRunTool(run: Pick<PlaygroundRun, 'toolType' | 'inputSnapshot'>) {
  const tool = TOOL_LABELS[run.toolType]
  if (run.toolType !== 'reference-builder') return tool
  const kind = run.inputSnapshot?.referenceBuilder?.kind
  return kind
    ? {
        ...tool,
        label: kind === 'character-sheet' ? 'Character Sheet' : 'Scale Reference',
        icon: kind === 'character-sheet' ? CharacterIcon : CompositeIcon
      }
    : tool
}
